import { platformAdminIdentityCustomFields } from "./platform-admin-identity-custom-fields";
import { platformAdminIdentityOrganizationTree } from "./platform-admin-identity-organization-tree";
import { platformAdminIdentityUserWorkflows } from "./platform-admin-identity-user-workflows";
import { platformAdminIdentityValues } from "./platform-admin-identity-values";
import { platformAdminIdentityWorkflows } from "./platform-admin-identity-workflows";
export const platformAdminIdentity = {
  duration1Day: "1 day",
  duration1Week: "1 week",
  duration1Month: "1 month",
  duration1Year: "1 year",

  title: "Identity",
  subtitle:
    "Current state of platform identity master data, identity-connected applications, and platform roles.",
  directRead:
    "This page is directly reading identity master data from sso-center, and write operations are also sent directly to the identity center.",
  indirectRead:
    "The current session is not signed in with unified identity. This page only shows link state until you switch to unified identity login.",
  loadFailed: "Failed to load identity data",
  summaryMode: "Auth mode: {value}",
  summarySso: "SSO status: {value}",
  summaryUsers: "Active users: {count}",
  summaryApps: "Identity-connected applications: {count}",
  summaryTenants: "Tenants: {count}",
  summaryOrgs: "Organizations: {count}",
  summaryRoles: "Platform roles: {count}",
  authCardTitle: "Authentication Mode",
  authModeCurrent: "Current auth_mode",
  ssoTarget: "SSO target",
  identityDomain: "Identity domain",
  consoleCardTitle: "Identity Console Info",
  links: {
    portal: "Portal",
    platformAdmin: "Platform Admin",
  },
  menu: {
    identity: "Identity Governance",
    userCustomFields: "User Custom Fields",
  },
  common: {
    pageNotFound: "Page not found.",
    backToIdentity: "Back to Identity Center",
  },
  login: {
    pageTitle: "{platformName} · Identity Center",
    summary:
      "Platform workspaces first take over the current unified identity session, then enter the target control plane.",
    maintenanceNotice:
      "Identity console is under maintenance. Some governance capabilities may be temporarily unavailable.",
    noAccess: "The current identity does not have access to the identity console.",
  },
  dashboard: {
    title: "Identity Overview",
    subtitle:
      "The home page of the identity governance site, summarizing application onboarding, users, tenants, organizations, roles, and permission maintenance entry points.",
    cards: {
      applications: "Application Onboarding Governance",
      users: "Platform User Governance",
      roles: "Role & Permission Governance",
    },
  },
  overview: {
    kpiActiveUsers: "Active Users",
    kpiApplications: "Applications",
    kpiTenants: "Tenants",
    kpiOrganizations: "Organizations",
    kpiRoles: "Platform Roles",
    kpiCriticalRisks: "Critical Risks",
    accessStatusTitle: "Access & Authentication Status",
    masterDataTitle: "Identity Master Data Scale",
    riskTitle: "Governance Risk",
    metricSsoStatus: "SSO Status",
    metricBlockedApplications: "Blocked Applications",
    metricDegradedApplications: "Degraded Applications",
    metricCredentialIssues: "Credential-Issue Applications",
    recentEventsSubtitle: "Recent 8 Identity Governance Events",
  },
  sections: {
    applications: {
      title: "Identity Applications",
      subtitle:
        "Maintain the application catalog, grant rules, application role mappings, and credentials in unified identity.",
    },
    tenants: {
      title: "Tenants",
      subtitle:
        "Maintain tenant master data in unified identity and preserve boundaries for future multi-tenant scope control.",
    },
    organizations: {
      title: "Organizations",
      subtitle:
        "Maintain the organization tree, node memberships, and organization scope in one place.",
    },
    users: {
      title: "Platform Users",
      subtitle:
        "Maintain basic user info, platform roles, application role mappings, and application context.",
    },
    userCustomFields: {
      title: "User Custom Fields",
      subtitle:
        "Maintain platform-user extension field definitions for unified identity profile modeling.",
    },
    roles: {
      title: "Platform Roles",
      subtitle: "Maintain the platform role catalog and its default governance boundaries.",
    },
    permissions: {
      title: "Permissions",
      subtitle:
        "Maintain the permission dictionary and bindings as the unified permission master data for platform governance and app consumption.",
    },
    events: {
      title: "Identity Audit",
      subtitle:
        "Trace who performed which governance action on which identity object, and when it happened.",
    },
    loginRecords: {
      title: "Login Records",
      subtitle:
        "Inspect real login attempts, results, source IPs, and captured client environment details.",
    },
  },
  objects: {
    application: "Identity Application",
    organizationUnit: "Organization Node",
    grant: "Access Rule",
    roleMapping: "Role Mapping",
    permissionBinding: "Permission Binding",
    tenant: "Tenant",
    user: "User",
    role: "Platform Role",
    permission: "Permission",
    userCustomField: "User Custom Field",
  },
  roleLabels: {
    platform_admin: "Platform Admin",
    intelligence_admin: "Intelligence Admin",
    identity_admin: "Identity Admin",
    asset_admin: "Asset Admin",
    asset_editor: "Asset Editor",
    execution_admin: "Execution Admin",
    environment_admin: "Environment Admin",
    environment_designer: "Environment Designer",
    tactics_techniques_admin: "Tactics and Techniques Admin",
    capability_graph_admin: "Capability Graph Admin",
    vulnerability_library_admin: "Vulnerability Library Admin",
    business_reference_admin: "Business Reference Center Admin",
    traffic_library_admin: "Traffic Library Admin",
    platform_operator: "Platform Operator",
    auditor: "Auditor",
    harbor_admin: "Harbor Admin",
    redblue_admin: "RedBlue Admin",
    redblue_activity_manager: "RedBlue Activity Manager",
    admin: "Administrator",
    user: "User",
    owner: "Owner",
    tester: "Tester",
    student: "Student",
    candidate: "Candidate",
    question_editor: "Question Editor",
    exam_manager: "Exam Manager",
    reviewer: "Reviewer",
    proctor: "Proctor",
  },
  fallbackPermissions: {
    consoleReadName: "View Identity Console",
    consoleReadDescription: "View the identity governance console overview and current state.",
    identityReadName: "View Identity Center",
    identityReadDescription:
      "View unified identity, applications, tenants, organizations, and user context.",
    identityManageName: "Manage Identity Center",
    identityManageDescription:
      "Maintain platform roles, permissions, bindings, tenants, organizations, and application onboarding rules.",
    assetReadName: "View Asset Center",
    assetReadDescription: "View assets, object spaces, and reference relationships.",
    assetManageName: "Manage Asset Center",
    assetManageDescription: "Maintain assets, upload policies, and object-space grants.",
    executionReadName: "View Execution Fabric",
    executionReadDescription: "View instances, tasks, and image execution state.",
    executionManageName: "Manage Execution Fabric",
    executionManageDescription: "Maintain instances, runtime resources, and execution tasks.",
    auditReadName: "View Platform Audit",
    auditReadDescription: "View login logs, operation logs, and platform audit records.",
  },
  roleModel: "Platform role model",
  componentTitle: "Identity Component",
  openSso: "Open SSO",
  footerNote:
    "This page now directly maintains sso-center identity-connected applications, tenants, organizations, platform role definitions, permissions, role bindings, permission bindings, application role mappings, and identity governance events.",
  tabs: {
    applications: "Identity Applications ({count})",
    tenants: "Tenants ({count})",
    organizations: "Organizations ({count})",
    users: "Platform Users ({count})",
    userCustomFields: "User Custom Fields ({count})",
    roles: "Platform Roles ({count})",
    permissions: "Permissions ({count})",
    events: "Identity Audit",
    loginRecords: "Login Records",
  },
  tables: {
    applications: "Identity Applications",
    tenants: "Tenants",
    organizations: "Organizations",
    roleDict: "Platform Role Dictionary",
    permissionDict: "Permission Dictionary",
    applicationGrantRules: "Application Access Rules",
    applicationRoleMappings: "Application Role Mappings",
    credentials: "Access Credentials",
    applicationAccess: "Application Access",
    businessRoleMatches: "Role Mapping Matches",
    permissionMatches: "Permission Matches",
    permissionBindings: "Permission Bindings",
  },
  columns: {
    component: "Component",
    domain: "Domain",
    latency: "Latency",
    note: "Note",
    application: "Application",
    portalDescription: "Portal Description",
    applicationKey: "Linked Service Code",
    entry: "Registry Entry URL",
    sortOrder: "Sort Order",
    grantRules: "Access Rules",
    roleKey: "Role Key",
    displayName: "Display Name",
    scope: "Scope",
    systemManaged: "System Managed",
    membershipCount: "Memberships",
    applicationGrantCount: "Access Rule Count",
    mappingCount: "Mappings",
    permissionKey: "Permission Key",
    applicationOwner: "Application",
    resourceAction: "Resource / Action",
    bindingCount: "Bindings",
    tenant: "Tenant",
    tenantKey: "Tenant Key",
    organization: "Organization",
    organizationKey: "Organization Key",
    tenantOwner: "Tenant",
    account: "Username",
    fullName: "Nickname",
    onlineStatus: "Online Status",
    mustChangePassword: "Must Change Password",
    failedLoginAttempts: "Failed Attempts",
    lockedUntil: "Locked Until",
    lastLoginAt: "Last Login",
    businessRole: "Business Role",
    platformRole: "Platform Role",
    applicationRoles: "Application Roles",
    organizationMemberships: "Organization Memberships",
    source: "Source",
    accessible: "Accessible",
    matchReason: "Match Reason",
    matchedMapping: "Matched Mapping",
    permission: "Permission",
    matchedBinding: "Matched Binding",
    eventType: "Event Category",
    action: "Operation",
    resourceType: "Object Type",
    resourceIdentifier: "Audit Object",
    operator: "Actor",
    time: "Occurred At",
    grantSubject: "Access Subject",
    subjectValue: "Subject Value",
    accessLevel: "Access Level",
    subjectType: "Subject Type",
    priority: "Priority",
    secretHint: "Secret Hint",
    expiresAt: "Expires At",
    rotatedAt: "Rotated At",
    targetApplication: "Target Application",
    objectValue: "Subject Value",
    clientId: "Client ID",
    clientSecret: "Client Secret",
    tokenEndpointAuthMethod: "Client Auth Method",
    payloadSummary: "Evidence Summary",
    payloadJson: "Raw Evidence JSON",
    eventId: "Audit ID",
    recordUpdatedAt: "Record Updated At",
    loginRecordId: "Login Record ID",
    loginTime: "Login Time",
    loginStatus: "Login Result",
    authFlow: "Auth Flow",
    failureReason: "Failure Reason",
    clientIp: "Client IP",
    userAgent: "UA",
    browserLanguage: "Browser Language",
    screenResolution: "Screen Resolution",
    terminalType: "Terminal Type",
    os: "Operating System",
    browser: "Browser",
  },
  values: platformAdminIdentityValues,
  actions: {
    ...platformAdminIdentityWorkflows.actions,
    ...platformAdminIdentityUserWorkflows.actions,
  },
  drawers: {
    ...platformAdminIdentityWorkflows.drawers,
    ...platformAdminIdentityUserWorkflows.drawers,
  },
  customFields: platformAdminIdentityCustomFields,
  alerts: {
    ...platformAdminIdentityWorkflows.alerts,
    ...platformAdminIdentityUserWorkflows.alerts,
  },
  confirms: {
    ...platformAdminIdentityWorkflows.confirms,
    ...platformAdminIdentityUserWorkflows.confirms,
  },
  messages: {
    ...platformAdminIdentityWorkflows.messages,
    ...platformAdminIdentityUserWorkflows.messages,
  },
  organizationTree: platformAdminIdentityOrganizationTree,
  forms: {
    ...platformAdminIdentityWorkflows.forms,
    ...platformAdminIdentityUserWorkflows.forms,
  },
};
